import {
	BottomWrapper,
	CheckboxWrapper,
	Container,
	DocumentCardsWrapper,
	DropdownDeleteField,
	ErrorText,
	MiddleWrapper,
	RedText,
	ScrollableSection,
	SelectionWrapper,
	TopWrapper,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/components/documentSectionStyledComponents';
import { DocumentCard } from '@dealstack/frontend/shared/components/DocumentCard/DocumentCard';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import {
	RightIcon,
	StringInput as StringInputV2,
} from '@dealstack/frontend/shared/components/forms/StringInput/StringInput.v2';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import {
	Checkbox as CheckboxV2,
	CheckboxV2Variant,
} from '@dealstack/frontend/shared/components/fundament/Checkbox/Checkbox.v2';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { SortingOrder } from '@dealstack/shared/lib/frontend/common-ui/types';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { DOCUMENT_WORKFLOW } from '@dealstack/shared/tests/testElementIds';
import {
	CreateDocumentWorkflowFileRes,
	DocumentTemplateType,
	DocumentWorkflowFileTct,
	GeneratableDocumentWorkflowTemplateType,
} from '@dealstack/shared/lib/generated';
import { useUploadDocumentFileController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/components/useUploadDocumentFileController';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { removeDocumentFile } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { debounce } from 'lodash';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
	DocumentSettings,
	TemplateTypesOptions,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/types';
import { getDocumentsFilesData } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';

export enum SEARCH_DOCUMENT_FILES_FORM {
	SEARCH = 'search',
}

export const ELEMENT_GAP = 8; //px
export const ELEMENT_SIZE = 220; //px

interface Props {
	className?: string;
	isUploadingGuidanceNote: boolean;
	documentsDataArray: DocumentSettings[];
	selectedDocumentWorkflowId: string;
	documentFiles: DocumentWorkflowFileTct[];
	showSelectedDocuments: boolean;
	documentsFilesSearch: string | undefined;
	documentsFilesSorting: SortingOrder;
	activeCheckboxesIds: string[];
	openedDropdownDocumentCardId: string | undefined;
	toggleCheckboxState: (documentWorkflowFileId: string) => void;
	onClickDropdown: (documentWorkflowFileId: string) => void;
	onCloseDropdown: () => void;
	setShowSelectedDocuments: Dispatch<SetStateAction<boolean>>;
	setDocumentsFilesSearch: Dispatch<SetStateAction<string | undefined>>;
	setDocumentsFilesSorting: Dispatch<SetStateAction<SortingOrder>>;
	setDocumentsDataArray: Dispatch<SetStateAction<DocumentSettings[]>>;
	isDocumentInDataArray: (documentWorkflowFileId: string) => boolean;
	changeDocumentTemplateType: (
		documentWorkflowFileId: string,
		templateType: TemplateTypesOptions['name']
	) => void;
	setSelectedDocumentWorkflowId: Dispatch<SetStateAction<string>>;
}

const DocumentSection = ({
	className,
	isUploadingGuidanceNote,
	documentsDataArray,
	selectedDocumentWorkflowId,
	documentFiles,
	showSelectedDocuments,
	documentsFilesSearch,
	documentsFilesSorting,
	activeCheckboxesIds,
	openedDropdownDocumentCardId,
	toggleCheckboxState,
	onClickDropdown,
	onCloseDropdown,
	setShowSelectedDocuments,
	setDocumentsFilesSearch,
	setDocumentsFilesSorting,
	isDocumentInDataArray,
	changeDocumentTemplateType,
	setSelectedDocumentWorkflowId,
	setDocumentsDataArray,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const [newDocumentsInCurrentSession, setNewDocumentInCurrentSession] = useState<string[]>([]);
	const [showSearchResult, setShowSearchResults] = useState<boolean>(false);

	const getDocumentsFilesQuery = useInfiniteQuery({
		queryKey: [
			API_PATHS.MIP_SIMPLE_DOCUMENT_WORKFLOW_DOCUMENT_FILES_GET,
			documentsFilesSearch,
			documentsFilesSorting,
		],

		initialPageParam: '',
		queryFn: async ({ pageParam }) =>
			await getDocumentsFilesData(API_PATHS.MIP_SIMPLE_DOCUMENT_WORKFLOW_DOCUMENT_FILES_GET, {
				search: documentsFilesSearch,
				cursor: pageParam,
				sortingOrder: documentsFilesSorting,
			}),

		getNextPageParam: (lastPage) => lastPage.payload?.nextCursor,
	});

	const onUploadNewDocument = async (response: ApiV1.Response<CreateDocumentWorkflowFileRes>) => {
		setNewDocumentInCurrentSession((prevState) => {
			if (!response?.payload?.id) {
				return prevState;
			}
			return [...prevState, response.payload.id];
		});
		await getDocumentsFilesQuery.refetch();
	};

	const { getInputProps, onUploadButton, fileRejections, isUploadError, isLoading } =
		useUploadDocumentFileController(onUploadNewDocument);

	const removeDocument = useMutation({
		mutationFn: removeDocumentFile,

		async onSuccess() {
			await getDocumentsFilesQuery.refetch();
		},
	});

	const removeSelectedDocument = (documentWorkflowFileId: string) => {
		const hasDocument = !!documentsDataArray.find(
			(d) => d.documentWorkflowFileId === documentWorkflowFileId
		);
		if (hasDocument) {
			setDocumentsDataArray((documentTemplateData: DocumentSettings[]) =>
				documentTemplateData.filter(
					(d) => d.documentWorkflowFileId !== documentWorkflowFileId
				)
			);
		}
		setSelectedDocumentWorkflowId('');
	};

	const onRemoveDocumentFile = (id: string) => {
		removeDocument.mutate({ documentWorkflowFileId: id });
		if (activeCheckboxesIds.includes(id)) {
			toggleCheckboxState(id);
		}
		removeSelectedDocument(id);
		onCloseDropdown();
	};

	const showInvalidTypeView = fileRejections?.some((rejection) =>
		rejection.errors.some((error) => error.code === 'file-invalid-type')
	);

	const onClickRecencyButton = () => {
		if (documentsFilesSorting === SortingOrder.ASC) {
			setDocumentsFilesSorting(SortingOrder.DESC);
		} else {
			setDocumentsFilesSorting(SortingOrder.ASC);
		}
	};

	const searchDocumentFilesForm = useForm({
		mode: 'onChange',
		defaultValues: { [SEARCH_DOCUMENT_FILES_FORM.SEARCH]: '' },
	});

	const onInputChange = () => {
		const searchValue = searchDocumentFilesForm.getValues(SEARCH_DOCUMENT_FILES_FORM.SEARCH);
		setDocumentsFilesSearch(searchValue);
	};

	const onDebouncedChange = useMemo(() => debounce(onInputChange, 500), [onInputChange]);

	const onResetSearchInput = async () => {
		searchDocumentFilesForm.setValue(SEARCH_DOCUMENT_FILES_FORM.SEARCH, '');
		setShowSearchResults(false);
		setDocumentsFilesSearch(undefined);
	};

	useEffect(() => {
		const searchValue = searchDocumentFilesForm.getValues(SEARCH_DOCUMENT_FILES_FORM.SEARCH);
		if (!searchValue.length) {
			return;
		}
		setShowSearchResults(true);
	}, [searchDocumentFilesForm.watch()]);

	const onClickShowOnlySelected = () => {
		setShowSelectedDocuments(!showSelectedDocuments);
	};

	const parentRef = useRef(null);

	const documentFilesNumber = documentFiles?.length;

	const rowVirtualizer = useVirtualizer({
		count: getDocumentsFilesQuery?.hasNextPage ? documentFilesNumber + 1 : documentFilesNumber,
		getScrollElement: () => parentRef.current,
		estimateSize: () => ELEMENT_GAP + ELEMENT_SIZE,
		horizontal: true,
		overscan: 0,
	});

	useEffect(() => {
		const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();
		if (!lastItem) {
			return;
		}

		if (
			!showSelectedDocuments &&
			lastItem?.index >= documentFilesNumber - 1 &&
			getDocumentsFilesQuery.hasNextPage &&
			!getDocumentsFilesQuery.isFetchingNextPage
		) {
			void getDocumentsFilesQuery.fetchNextPage();
		}
	}, [
		getDocumentsFilesQuery.hasNextPage,
		getDocumentsFilesQuery.isFetchingNextPage,
		getDocumentsFilesQuery.fetchNextPage,
		documentFilesNumber,
		rowVirtualizer.getVirtualItems(),
		showSelectedDocuments,
	]);

	useEffect(() => {
		if (activeCheckboxesIds.length) {
			return;
		}

		setShowSelectedDocuments(false);
	}, [activeCheckboxesIds]);

	const tPrefix = 'investors.simple_document_workflow.document_section';
	const sortingTranslationKey = `${tPrefix}.buttons.sorting${
		documentsFilesSorting === SortingOrder.ASC ? '' : '_desc'
	}`;

	const totalRecord = getDocumentsFilesQuery?.data?.pages?.map(
		(page) => page?.payload?.totalRecord
	)[0];

	const selectDocumentCard = ({
		id: documentWorkflowFileId,
		isGDPR,
		isPreFillableForm,
		generatableDocumentWorkflowTemplateType,
	}: DocumentWorkflowFileTct) => {
		const setCustomForFirstTime = !isDocumentInDataArray(documentWorkflowFileId);
		setSelectedDocumentWorkflowId(documentWorkflowFileId);

		if (!activeCheckboxesIds?.includes(documentWorkflowFileId)) {
			toggleCheckboxState(documentWorkflowFileId);
		}

		if (isGDPR) {
			changeDocumentTemplateType(documentWorkflowFileId, DocumentTemplateType.GDPR_NOTICE);
			return;
		}

		if (isPreFillableForm) {
			changeDocumentTemplateType(
				documentWorkflowFileId,
				DocumentTemplateType.PRE_FILLABLE_FORM
			);
			return;
		}

		if (
			generatableDocumentWorkflowTemplateType ===
			GeneratableDocumentWorkflowTemplateType.ANNUAL_STATEMENT
		) {
			changeDocumentTemplateType(documentWorkflowFileId, DocumentTemplateType.ANNUAL_REPORT);
			return;
		}

		if (
			generatableDocumentWorkflowTemplateType ===
			GeneratableDocumentWorkflowTemplateType.FORM_S431
		) {
			changeDocumentTemplateType(documentWorkflowFileId, DocumentTemplateType.FORM_S431);
			return;
		}

		if (setCustomForFirstTime) {
			changeDocumentTemplateType(documentWorkflowFileId, DocumentTemplateType.CUSTOM);
		}
	};

	const documentsValueText = useMemo(() => {
		if (showSearchResult) {
			return (
				<Container>
					<Icon variant="arrow-left-v3" onClick={onResetSearchInput} />
					<TextV2 variant="sans/14/600">
						{tMipManage(`${tPrefix}.search_results`, {
							numberOfSearchedDocuments: totalRecord?.toString(),
						})}
					</TextV2>
				</Container>
			);
		} else {
			return (
				<TextV2 variant="sans/14/600">
					{tMipManage(`${tPrefix}.documents`, {
						numberOfDocuments: totalRecord?.toString(),
					})}
				</TextV2>
			);
		}
	}, [showSearchResult, onResetSearchInput, getDocumentsFilesQuery]);

	if (getDocumentsFilesQuery?.isLoading) {
		return <InlineLoader />;
	}

	return (
		<div className={className}>
			{getDocumentsFilesQuery?.isLoading ? <InlineLoader /> : null}
			<TopWrapper>
				<Container>
					{documentsValueText}
					<input {...getInputProps()} />
					{isLoading ? (
						<ButtonV2
							icon="thinking-loading"
							disabled
							size="s"
							variant="secondary"
							buttonTheme="light"
							leftIcon
							className={isLoading ? 'spinner' : ''}
						>
							{tMipManage(`${tPrefix}.buttons.uploading`)}
						</ButtonV2>
					) : (
						<ButtonV2
							onClick={onUploadButton}
							size="s"
							variant="secondary"
							buttonTheme="light"
							icon="upload"
							leftIcon
						>
							{tMipManage(`${tPrefix}.buttons.new_upload`)}
						</ButtonV2>
					)}
					{showInvalidTypeView && (
						<ErrorText variant="sans/14/600">
							{tMipManage(`${tPrefix}.invalid_type_error`)}
						</ErrorText>
					)}
					{isUploadError && (
						<ErrorText variant="sans/14/600">
							{tMipManage(`${tPrefix}.upload_error`)}
						</ErrorText>
					)}
				</Container>
				<ButtonV2
					onClick={onClickRecencyButton}
					size="s"
					variant="ghost"
					buttonTheme="light"
					icon={
						documentsFilesSorting === SortingOrder.ASC ? 'arrow-up-v3' : 'arrow-down-v3'
					}
					leftIcon
				>
					{tMipManage(sortingTranslationKey)}
				</ButtonV2>
			</TopWrapper>
			<MiddleWrapper>
				<FormProvider {...searchDocumentFilesForm}>
					<StringInputV2
						onInputChange={onDebouncedChange}
						leftIcon="search-v3"
						rightIcon={
							!!documentsFilesSearch &&
							(getDocumentsFilesQuery?.isLoading ? (
								'thinking-loading'
							) : (
								<ClearIcon onClick={onResetSearchInput} variant="close-v3" />
							))
						}
						inputPlaceHolder="Search document"
						name="search"
						className={className}
					/>
				</FormProvider>
			</MiddleWrapper>
			<BottomWrapper>
				<SelectionWrapper>
					<TextV2 variant="sans/14/600" data-testid={DOCUMENT_WORKFLOW.SELECT_LABEL}>
						{tMipManage(`${tPrefix}.selected`, {
							numberOfDocuments: activeCheckboxesIds.length.toString(),
						})}
					</TextV2>
					<CheckboxWrapper>
						<CheckboxV2
							size="s"
							variant={
								showSelectedDocuments ? CheckboxV2Variant.on : CheckboxV2Variant.off
							}
							onClick={onClickShowOnlySelected}
							disabled={!activeCheckboxesIds.length}
						/>
						<TextV2 variant="sans/14/400">
							{tMipManage(`${tPrefix}.show_selected`)}
						</TextV2>
					</CheckboxWrapper>
				</SelectionWrapper>
				<DocumentCardsWrapper ref={parentRef}>
					<ScrollableSection scrollTotalSize={rowVirtualizer.getTotalSize()}>
						{rowVirtualizer.getVirtualItems().map((virtualRow) => {
							const document = documentFiles && documentFiles[virtualRow.index];

							if (!document) {
								return null;
							}

							const isNewDocument = newDocumentsInCurrentSession.includes(
								document.id
							);

							return (
								<DocumentCardInternal
									documentTitle={document.name}
									documentID={document.id}
									date={formatStringDateToUI(
										document.createdAt,
										DATE_FORMATS.UI_SHORT
									)}
									showDate={
										!document.isGeneratableDocument &&
										!document.isPreFillableForm
									}
									isSelected={document.id === selectedDocumentWorkflowId}
									onClickCard={() => {
										selectDocumentCard(document);
									}}
									checkboxValue={activeCheckboxesIds?.includes(document.id)}
									onClickCheckbox={() => toggleCheckboxState(document.id)}
									isNew={isNewDocument}
									key={document.id}
									isDropdownOpen={openedDropdownDocumentCardId === document.id}
									onClickDropdown={() => onClickDropdown(document.id)}
									onCloseDropdown={() => onCloseDropdown()}
									$size={virtualRow.size}
									$offset={virtualRow.start}
									disabled={isUploadingGuidanceNote}
								>
									<DocumentCard.DropdownMenu>
										<DropdownDeleteField
											onClick={() => {
												if (!document.canDelete) {
													return;
												}

												onRemoveDocumentFile(document.id);
											}}
										>
											<RedText
												variant="sans/11/400"
												disabled={!document.canDelete}
											>
												{tMipManage(`${tPrefix}.dropdown_menu.delete`)}
											</RedText>
										</DropdownDeleteField>
									</DocumentCard.DropdownMenu>
								</DocumentCardInternal>
							);
						})}
					</ScrollableSection>
				</DocumentCardsWrapper>
			</BottomWrapper>
		</div>
	);
};

const DocumentCardInternal = styled(DocumentCard)<{
	$offset: number;
	$size: number;
}>`
	width: ${({ $size }) => `${$size - ELEMENT_GAP}px`};
	position: absolute;
	left: 0;
	top: 0;
	transform: ${({ $offset }) => `translateX(${$offset}px);`};
`;

const ClearIcon = styled(Icon)`
	left: 44.5rem;
`;

const Styled = styled(DocumentSection)`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	border-radius: 0.5rem;
	padding: 0.5rem;
	background-color: ${defaultTheme.v2.color.neutral[100]};

	.spinner {
		svg {
			animation-name: spin;
			animation-duration: 5000ms;
			animation-iteration-count: infinite;
			animation-timing-function: linear;
			@keyframes spin {
				from {
					transform: rotate(0deg);
				}
				to {
					transform: rotate(360deg);
				}
			}
		}
	}

	${StringInputV2} {
		${RightIcon} {
			animation-name: spin;
			animation-duration: 5000ms;
			animation-iteration-count: infinite;
			animation-timing-function: linear;
		}

		@keyframes spin {
			from {
				transform: rotate(0deg);
			}
			to {
				transform: rotate(360deg);
			}
		}
	}

	${DocumentCardInternal} {
		min-height: 4.875rem;
	}
`;

export { Styled as DocumentSection };
